import React, { useState, useCallback, useMemo, useRef } from "react";
import { useDataQualityAnalytics, fetchExerciseUsers } from "../hooks/useAnalytics";
import {
  AnalyticsFilters,
  FlaggedExercise,
  DataQualityData,
  ExerciseUser,
} from "../types/analytics.types";
import { KPICard } from "./KPICard";
import { callOpenAI } from "../../API/API";
import apiClient from "../../services/apiClient";
import { dataLoaderUri } from "../../API/API";
import "./DataQualityTab.css";

interface DataQualityTabProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

// Build an AI review prompt based on exercise data
function buildReviewPrompt(exercise: FlaggedExercise): string {
  const meta = exercise.metadata || {};
  const component = exercise.component;

  // Context line with proficiency, difficulty, and domain when available
  const proficiency = meta.proficiencyLevel || meta.proficiency_level || "";
  const difficulty = meta.difficulty || "";
  const domain = meta.domain || "";
  const contextParts: string[] = [];
  if (proficiency) contextParts.push(`Proficiency: ${proficiency}`);
  if (difficulty) contextParts.push(`Difficulty: ${difficulty}`);
  if (domain) contextParts.push(`Domain: ${domain}`);
  const contextLine = contextParts.length > 0 ? `\n${contextParts.join(" | ")}` : "";

  let exerciseDetails = "";
  let componentChecks = "";

  // Detect vocabulary-sourced exercises that should use vocab branch regardless of component name
  const isVocabSource =
    meta._source === "VocabularyTable" || meta._source === "CustomVocabularyItems";

  if (
    component.includes("Sentence Translation") ||
    component === "Text_To_Translation" ||
    component === "SentenceTranslation" ||
    component === "Sentence_Translation" ||
    component === "AvatarSentenceReconstruction" ||
    component === "Pronunciation" ||
    component === "Speech-Sentence"
  ) {
    exerciseDetails = `
Component Type: Sentence Translation / Reconstruction
Source Sentence: ${meta.sentenceSource || meta.baseExampleSentence || "N/A"}
Target Sentence: ${meta.sentenceTarget || meta.targetExampleSentence || "N/A"}
Words Ordered (Base): ${JSON.stringify(meta.wordsOrderedBase || [])}
Words Ordered (Target): ${JSON.stringify(meta.wordsOrderedTarget || [])}
Distractors (Base): ${JSON.stringify(meta.distractorsBase || [])}
Distractors (Target): ${JSON.stringify(meta.distractorsTarget || [])}`;
  } else if (
    !isVocabSource &&
    (component.includes("Fill_in_the_Blank") ||
      component.includes("Multiple_Choice") ||
      component === "Fill in the Blank" ||
      component === "Fill in the blank" ||
      component === "Multiple Choice" ||
      component === "Spell_The_Blank" ||
      component === "Spell The Blank" ||
      component === "SpellTheBlank")
  ) {
    const blanks = meta.blanks || [];
    const blanksExplanation = Array.isArray(blanks)
      ? blanks
          .map(
            (b: any, i: number) =>
              `  Blank ${i + 1}: correctAnswer="${b.correctAnswer || b.answer || "?"}", options=${JSON.stringify(b.options || [])}`
          )
          .join("\n")
      : JSON.stringify(blanks);
    exerciseDetails = `
Component Type: ${component}
Sentence with blank: ${meta.sentenceText || "N/A"}
Blanks:
${blanksExplanation}

The learner sees the sentence with a blank (___) and must pick the correct option from the options list. The "correctAnswer" is what the app considers the right answer. The "options" array contains ALL choices shown to the learner (including the correct one).`;
    componentChecks = `
- FILL IN THE BLANK SPECIFIC: Does the correct answer fit grammatically in the blank? Could any other option also validly complete the sentence? If multiple options could work, that's a problem.`;
  } else if (component === "ConversationResponse") {
    exerciseDetails = `
Component Type: Conversation Response
Prompt (what the other speaker says): ${meta.promptSentence || "N/A"}
Prompt Translation: ${meta.promptTranslation || "N/A"}
Correct Response: ${meta.correctResponse || "N/A"}
Incorrect Option 1: ${meta.incorrectResponse1 || "N/A"}
Incorrect Option 2: ${meta.incorrectResponse2 || "N/A"}
Incorrect Option 3: ${meta.incorrectResponse3 || "N/A"}

The learner hears/reads the prompt and must select the correct conversational reply from 4 options.`;
    componentChecks = `
- CONVERSATION SPECIFIC: Is the correct response a natural reply to the prompt? Would any incorrect option also be a natural/appropriate reply? Check that the prompt and response form a coherent conversational exchange.`;
  } else if (
    component === "SentenceComprehension" ||
    component === "Listening_Comprehension" ||
    component === "Reading_Comprehension" ||
    component.includes("Comprehension")
  ) {
    exerciseDetails = `
Component Type: Sentence Comprehension
Sentences (Target Language): ${JSON.stringify(meta.sentencesTarget || [])}
Sentences (Base Language): ${JSON.stringify(meta.sentencesBase || [])}
Correct Summary (Base): ${meta.correctSummaryBase || "N/A"}
Correct Summary (Target): ${meta.correctSummaryTarget || "N/A"}
Incorrect Summary 1 (Base): ${meta.incorrectSummary1Base || "N/A"} | (Target): ${meta.incorrectSummary1Target || "N/A"}
Incorrect Summary 2 (Base): ${meta.incorrectSummary2Base || "N/A"} | (Target): ${meta.incorrectSummary2Target || "N/A"}
Incorrect Summary 3 (Base): ${meta.incorrectSummary3Base || "N/A"} | (Target): ${meta.incorrectSummary3Target || "N/A"}

The learner reads/listens to sentences and must select the summary that correctly describes the content. They choose from 4 summary options (1 correct + 3 incorrect).`;
    componentChecks = `
- COMPREHENSION SPECIFIC: Does the correct summary accurately describe the sentences? Could any incorrect summary also be interpreted as a valid description? Are the target-language and base-language versions of each summary consistent with each other?`;
  } else if (component === "Conversation") {
    exerciseDetails = `
Component Type: Conversation Objective
Objective (Base): ${meta.objectiveBaseText || "N/A"}
Objective (Target): ${meta.objectiveTargetText || "N/A"}

This defines a conversation learning objective. The learner practices a free-form conversation to achieve this objective.`;
    componentChecks = `
- OBJECTIVE SPECIFIC: Is the objective clearly stated and achievable? Is the translation between base and target accurate? Is the objective appropriate for the proficiency level?`;
  } else if (
    component === "Matching" ||
    component === "Flashcards" ||
    component === "SpeedGame" ||
    isVocabSource
  ) {
    exerciseDetails = `
Component Type: ${component} (Vocabulary — word translation matching)
Base Word (correct answer in base language): ${meta.base_word || meta.baseWord || "N/A"}
Target Word (correct answer in target language): ${meta.targ_word || meta.word || meta.targetWord || "N/A"}
Distractor Options (Base): ${JSON.stringify(meta.baseLangOptions || meta.base_lang_options || [])}
Distractor Options (Target): ${JSON.stringify(meta.targetLangOptions || meta.targ_lang_options || [])}
Example Sentence (Base): ${meta.baseExampleSentence || meta.base_example_sentence || "N/A"}
Example Sentence (Target): ${meta.targetExampleSentence || meta.target_example_sentence || "N/A"}

IMPORTANT CONTEXT: This is a VOCABULARY TRANSLATION exercise. The learner sees a word and must select its correct translation from multiple choices. The options arrays contain ONLY distractor (wrong) answers — the correct answer is added by the app automatically.

For this exercise type, distractors that are SIMILAR but DIFFERENT words (like "Good morning" as a distractor for "Good night") are GOOD distractors — they test whether the learner knows the exact translation. A distractor is only bad if it is an actual valid TRANSLATION or SYNONYM of the correct word.`;
  } else if (
    component === "ItemGuess" ||
    component === "Item Guess" ||
    component === "Item_Guess"
  ) {
    exerciseDetails = `
Component Type: Item Guess (multiple-choice image/description guessing)
Correct Option: ${meta.correctOption || "N/A"}
Option 1: ${meta.options?.[0] || "N/A"}
Option 2: ${meta.options?.[1] || "N/A"}
Option 3: ${meta.options?.[2] || "N/A"}
Option 4: ${meta.options?.[3] || "N/A"}
Hints: ${JSON.stringify(meta.hints || [])}

The learner reads descriptive hints and must guess which vocabulary item is being described by selecting from 4 options. Only one option should match the hints.`;
    componentChecks = `
- ITEM GUESS SPECIFIC: Do the hints clearly and unambiguously describe the correct option? Could any other option also match the hints? Are the hints accurate for the correct answer? Are any hints misleading?`;
  } else if (
    component === "ItemDescribe" ||
    component === "Item Describe" ||
    component === "Item_Describe" ||
    component === "ItemDescription" ||
    component === "Item Description"
  ) {
    exerciseDetails = `
Component Type: Item Describe (vocabulary description exercise)
Word (Base Language): ${meta.wordTextBase || meta.baseWord || "N/A"}
Word (Target Language): ${meta.wordTextTarget || meta.targetWord || "N/A"}

The learner must describe or identify a vocabulary word. This tests whether the learner understands the word's meaning well enough to describe it.`;
    componentChecks = `
- ITEM DESCRIBE SPECIFIC: Is the base-to-target translation accurate? Is the word appropriate for the proficiency level? Would the word be clear enough for a learner to describe?`;
  } else {
    exerciseDetails = `
Component Type: ${component}
Metadata: ${JSON.stringify(meta, null, 2)}`;
  }

  // Extract the target word from the word ID (format: #Targ_XX#Base_YY#word)
  const targetWordMatch = exercise.word_id?.match(/#[^#]+#[^#]+#(.+)$/);
  const targetWord = targetWordMatch ? targetWordMatch[1] : "";
  const targetWordNote = targetWord
    ? `\nTARGET WORD BEING PRACTICED: "${targetWord}" — This exercise exists specifically to help the learner practice this word. The word MUST appear in the exercise content. If the sentence uses this word unnaturally, the fix should rewrite the sentence to incorporate it naturally — NEVER remove the target word from the exercise.`
    : "";

  return `You are a strict language education QA reviewer. Your job is to find problems that cause learners to get exercises wrong NOT because they lack knowledge, but because the exercise itself is flawed.

Section: ${exercise.section} (thematic category — all content in this section should relate to this topic)
Word ID: ${exercise.word_id}
Target Language: ${meta.targetLanguage || meta.targ_lang_code || "Unknown"}
Base Language: ${meta.baseLanguage || meta.base_lang_code || "Unknown"}${contextLine}${targetWordNote}
${exerciseDetails}

This exercise has a ${exercise.failure_rate}% failure rate across ${exercise.unique_users} unique users (${exercise.total_attempts} total attempts). A high failure rate may indicate a data quality problem.

CRITICAL CHECKS (in order of importance):

1. INCORRECT CORRECT ANSWER: Is the designated "correct" answer actually wrong? Check for translation errors, grammatical mistakes, or wrong word usage.

2. TRANSLATION ACCURACY: Are all translations between base and target language accurate? Check for missing accents/diacritics (e.g., "Buenos dias" should be "Buenos días").

3. AMBIGUOUS OPTIONS: Are any of the "wrong" options ALSO valid answers?
   - For FILL IN THE BLANK / MULTIPLE CHOICE: A distractor is bad if it could also grammatically and semantically fit in the blank. The learner must be able to determine which option is correct from context alone.
   - For VOCABULARY MATCHING (Matching/Flashcards/SpeedGame): A distractor is bad ONLY if it is a valid translation or synonym of the correct word (i.e., a native speaker would accept it as a correct translation). Words from the same category that have different meanings (like "Good morning" vs "Good night") are GOOD distractors. However, if a word in the target language has multiple valid English translations and one of those translations appears as a distractor, that IS a problem (e.g., "Buenas noches" means both "Good night" and "Good evening", so "Good evening" as a distractor would be ambiguous).
   - For CONVERSATION RESPONSE: A distractor is bad if it would be a natural/appropriate reply to the prompt.
   - For ITEM GUESS: A distractor is bad if the hints could also describe it.
   - For SENTENCE COMPREHENSION: A distractor summary is bad if it could also be a valid interpretation of the sentences.

4. GRAMMATICAL CORRECTNESS: Is the sentence structure natural and grammatically correct in the target language?
${componentChecks}
VERDICT RULES:
- "incorrect" = The exercise has a clear flaw that would cause learners to fail (ambiguous options, wrong answer marked as correct, bad translation, missing accents)
- "ambiguous" = There is a potential issue but it's debatable
- "correct" = The exercise is well-formed, all options are unambiguous, and the high failure rate is likely due to learner difficulty, not data problems

Respond ONLY with valid JSON (no markdown, no backticks):
{"verdict": "correct" or "incorrect" or "ambiguous", "explanation": "detailed explanation — list each option and whether it's valid/invalid for the context", "suggested_fix": "specific fix if incorrect, or empty string"}`;
}

// Build an AI prompt to generate corrected exercise data
function buildFixPrompt(exercise: FlaggedExercise, reviewExplanation: string): string {
  const meta = exercise.metadata || {};
  const component = exercise.component;

  // Context line with proficiency, difficulty, and domain when available
  const proficiency = meta.proficiencyLevel || meta.proficiency_level || "";
  const difficulty = meta.difficulty || "";
  const contextParts: string[] = [];
  if (proficiency) contextParts.push(`Proficiency: ${proficiency}`);
  if (difficulty) contextParts.push(`Difficulty: ${difficulty}`);
  const contextLine = contextParts.length > 0 ? `\n${contextParts.join(" | ")}` : "";

  let fieldInstructions = "";
  let currentData = "";

  // Detect vocabulary-sourced exercises that should use vocab branch regardless of component name
  const isVocabSource =
    meta._source === "VocabularyTable" || meta._source === "CustomVocabularyItems";

  if (
    component.includes("Sentence Translation") ||
    component === "Text_To_Translation" ||
    component === "SentenceTranslation" ||
    component === "Sentence_Translation" ||
    component === "AvatarSentenceReconstruction" ||
    component === "Pronunciation" ||
    component === "Speech-Sentence"
  ) {
    currentData = `Current Data:
- sentenceSource: ${JSON.stringify(meta.sentenceSource || meta.baseExampleSentence || "")}
- sentenceTarget: ${JSON.stringify(meta.sentenceTarget || meta.targetExampleSentence || "")}
- wordsOrderedBase: ${JSON.stringify(meta.wordsOrderedBase || [])}
- wordsOrderedTarget: ${JSON.stringify(meta.wordsOrderedTarget || [])}
- distractorsBase: ${JSON.stringify(meta.distractorsBase || [])}
- distractorsTarget: ${JSON.stringify(meta.distractorsTarget || [])}`;
    fieldInstructions = `Editable fields: "sentenceSource", "sentenceTarget", "wordsOrderedBase" (array of strings), "wordsOrderedTarget" (array of strings), "distractorsBase" (array of strings), "distractorsTarget" (array of strings).
- wordsOrderedBase/Target must contain exactly the words of the sentence in order.
- distractors must be clearly wrong but plausible words — NEVER a word that could validly fit.`;
  } else if (
    !isVocabSource &&
    (component.includes("Fill_in_the_Blank") ||
      component.includes("Multiple_Choice") ||
      component === "Fill in the Blank" ||
      component === "Fill in the blank" ||
      component === "Multiple Choice" ||
      component === "Spell_The_Blank" ||
      component === "Spell The Blank" ||
      component === "SpellTheBlank")
  ) {
    currentData = `Current Data:
- sentenceText: ${JSON.stringify(meta.sentenceText || "")}
- blanks: ${JSON.stringify(meta.blanks || [])}`;
    fieldInstructions = `Editable fields: "sentenceText" (string), "blanks" (array of objects).
IMPORTANT: Each blank object MUST use this EXACT format: {"correctAnswer": "the correct answer", "options": ["option1", "option2", "option3", "option4"]}
- Use "correctAnswer" (NOT "answer"). Do NOT include "position" or any other fields.
- The "options" array must include the correctAnswer as one of the options.
- The correctAnswer must be the ONLY valid option. All other options must be clearly incorrect for the context.
- If the issue is an ambiguous distractor, replace it with one that is clearly wrong.`;
  } else if (component === "ConversationResponse") {
    currentData = `Current Data:
- promptSentence: ${JSON.stringify(meta.promptSentence || "")}
- promptTranslation: ${JSON.stringify(meta.promptTranslation || "")}
- correctResponse: ${JSON.stringify(meta.correctResponse || "")}
- incorrectResponse1: ${JSON.stringify(meta.incorrectResponse1 || "")}
- incorrectResponse2: ${JSON.stringify(meta.incorrectResponse2 || "")}
- incorrectResponse3: ${JSON.stringify(meta.incorrectResponse3 || "")}`;
    fieldInstructions = `Editable fields: "promptSentence", "promptTranslation", "correctResponse", "incorrectResponse1", "incorrectResponse2", "incorrectResponse3".
- The correctResponse must be the only natural/valid reply to the prompt.
- Each incorrectResponse must be clearly NOT a valid reply to the prompt.
- If you change promptSentence, update promptTranslation to match.`;
  } else if (
    component === "SentenceComprehension" ||
    component === "Listening_Comprehension" ||
    component === "Reading_Comprehension" ||
    component.includes("Comprehension")
  ) {
    currentData = `Current Data:
- sentencesTarget: ${JSON.stringify(meta.sentencesTarget || [])}
- sentencesBase: ${JSON.stringify(meta.sentencesBase || [])}
- correctSummaryBase: ${JSON.stringify(meta.correctSummaryBase || "")}
- correctSummaryTarget: ${JSON.stringify(meta.correctSummaryTarget || "")}
- incorrectSummary1Base: ${JSON.stringify(meta.incorrectSummary1Base || "")}
- incorrectSummary1Target: ${JSON.stringify(meta.incorrectSummary1Target || "")}
- incorrectSummary2Base: ${JSON.stringify(meta.incorrectSummary2Base || "")}
- incorrectSummary2Target: ${JSON.stringify(meta.incorrectSummary2Target || "")}
- incorrectSummary3Base: ${JSON.stringify(meta.incorrectSummary3Base || "")}
- incorrectSummary3Target: ${JSON.stringify(meta.incorrectSummary3Target || "")}`;
    fieldInstructions = `Editable fields: "sentencesTarget", "sentencesBase", "correctSummaryBase", "correctSummaryTarget", "incorrectSummary1Base", "incorrectSummary1Target", "incorrectSummary2Base", "incorrectSummary2Target", "incorrectSummary3Base", "incorrectSummary3Target".
- Incorrect summaries must be clearly wrong interpretations of the sentences.
- If you change a Base summary, update the corresponding Target summary to match (and vice versa).`;
  } else if (
    component === "Matching" ||
    component === "Flashcards" ||
    component === "SpeedGame" ||
    isVocabSource
  ) {
    // Check if it's CustomVocabularyItems or VocabularyTable based on field names
    const isCustomVocab = meta._source === "CustomVocabularyItems";
    if (isCustomVocab) {
      currentData = `Current Data:
- baseWord (correct answer in base language): ${JSON.stringify(meta.baseWord || "")}
- targetWord (correct answer in target language): ${JSON.stringify(meta.targetWord || "")}
- baseLangOptions (distractor wrong answers in base language): ${JSON.stringify(meta.baseLangOptions || [])}
- targetLangOptions (distractor wrong answers in target language): ${JSON.stringify(meta.targetLangOptions || [])}
- baseExampleSentence: ${JSON.stringify(meta.baseExampleSentence || "")}
- targetExampleSentence: ${JSON.stringify(meta.targetExampleSentence || "")}`;
      fieldInstructions = `Editable fields: "baseWord", "targetWord", "baseLangOptions" (array of strings), "targetLangOptions" (array of strings), "baseExampleSentence", "targetExampleSentence".
- The options arrays contain ONLY distractors (wrong answers). The correct answer is added by the app automatically. Options must NOT include the correct answer or synonyms of it.`;
    } else {
      currentData = `Current Data:
- base_word (correct answer in base language): ${JSON.stringify(meta.base_word || "")}
- targ_word (correct answer in target language): ${JSON.stringify(meta.targ_word || "")}
- base_lang_options (distractor wrong answers in base language): ${JSON.stringify(meta.base_lang_options || [])}
- targ_lang_options (distractor wrong answers in target language): ${JSON.stringify(meta.targ_lang_options || [])}
- base_example_sentence: ${JSON.stringify(meta.base_example_sentence || "")}
- target_example_sentence: ${JSON.stringify(meta.target_example_sentence || "")}`;
      fieldInstructions = `Editable fields: "base_word", "targ_word", "base_lang_options" (array of strings), "targ_lang_options" (array of strings), "base_example_sentence", "target_example_sentence".
IMPORTANT: Use these EXACT field names. Do NOT rename them (e.g., do NOT use "target_lang_options", use "targ_lang_options").
- The options arrays contain ONLY distractors (wrong answers). The correct answer is added by the app automatically. Options must NOT include the correct answer or synonyms of it.`;
    }
  } else if (
    component === "ItemGuess" ||
    component === "Item Guess" ||
    component === "Item_Guess"
  ) {
    currentData = `Current Data:
- correctOption: ${JSON.stringify(meta.correctOption || "")}
- options: ${JSON.stringify(meta.options || [])}
- hints: ${JSON.stringify(meta.hints || [])}`;
    fieldInstructions = `Editable fields: "correctOption", "options" (array of 4 strings), "hints" (array of strings).
- The correctOption must be one of the items in the options array.
- Hints must clearly and unambiguously describe ONLY the correctOption.
- Other options must NOT match the hints.
- Keep all options from the same thematic category (section: ${exercise.section}).`;
  } else if (
    component === "ItemDescribe" ||
    component === "Item Describe" ||
    component === "Item_Describe" ||
    component === "ItemDescription" ||
    component === "Item Description"
  ) {
    currentData = `Current Data:
- wordTextBase: ${JSON.stringify(meta.wordTextBase || meta.baseWord || "")}
- wordTextTarget: ${JSON.stringify(meta.wordTextTarget || meta.targetWord || "")}`;
    fieldInstructions = `Editable fields: "wordTextBase", "wordTextTarget".
- Ensure the base-to-target translation is accurate and properly accented.`;
  } else if (component === "Conversation") {
    currentData = `Current Data:
- objectiveBaseText: ${JSON.stringify(meta.objectiveBaseText || "")}
- objectiveTargetText: ${JSON.stringify(meta.objectiveTargetText || "")}`;
    fieldInstructions = `Editable fields: "objectiveBaseText", "objectiveTargetText".
- Ensure the objective translation is accurate between base and target language.
- The objective should be clear and achievable for the proficiency level.`;
  } else {
    currentData = `Current Data:\n${JSON.stringify(meta, null, 2)}`;
    fieldInstructions = `Output only the fields that need to be changed with their corrected values.`;
  }

  // Extract the target word from the word ID (format: #Targ_XX#Base_YY#word)
  const targetWordMatch = exercise.word_id?.match(/#[^#]+#[^#]+#(.+)$/);
  const targetWord = targetWordMatch ? targetWordMatch[1] : "";
  const targetWordNote = targetWord
    ? `\nTARGET WORD BEING PRACTICED: "${targetWord}" — This exercise exists to teach this word. Any fixed sentences MUST still contain this word (or its translation). NEVER remove the target word from the exercise.`
    : "";

  return `You are a language education data fixer. Given the AI review below and the current exercise data, output the CORRECTED data.

Component: ${component}
Section: ${exercise.section} (thematic category — replacements must stay within this topic)
Target Language: ${meta.targetLanguage || meta.targ_lang_code || "Unknown"}
Base Language: ${meta.baseLanguage || meta.base_lang_code || "Unknown"}${contextLine}${targetWordNote}

AI REVIEW DIAGNOSIS:
${reviewExplanation}

${currentData}

${fieldInstructions}

RULES:
1. Only include fields that NEED to change. Do NOT include unchanged fields — but if ANY item in an array needs to change, include the ENTIRE array with ALL items (both fixed and unchanged).
2. NEVER modify the core word pair (base_word/targ_word, baseWord/targetWord, correctOption for ItemGuess). These define the exercise and must stay the same. Only fix distractors, options, hints, or example sentences.
3. NEVER remove the target word being practiced from the exercise. The exercise exists to teach that word. If the sentence uses the word unnaturally, rewrite the sentence so it uses the word naturally — do NOT drop the word. The target word must appear in wordsOrderedBase/Target and in the sentences. For example, if the target word is "por favor", a fixed sentence must still include "por favor" in a natural context (e.g., "¿Puedes ayudarme, por favor?").
4. Keep the same language, difficulty level, and educational intent.
5. Replacement distractors MUST be from the SAME thematic category as the section "${exercise.section}" (e.g., if the section is greetings, all distractors should be greetings/farewells vocabulary — NOT unrelated words like "Thank you" or "Water").
6. Fix ALL issues identified in the AI review — ambiguous distractors, spelling errors, missing accents, grammatical errors. Do not leave known problems unfixed.
7. Ensure all translations are accurate and properly accented (e.g., "Buenos días" not "Buenos dias").
8. Array fields should be output as arrays (e.g., ["word1", "word2"]).
9. PREFER MINIMAL FIXES. If the problem is an ambiguous distractor, just replace that one distractor — do NOT rewrite the sentences or restructure the exercise. The smallest change that resolves the issue is the best fix.
10. NEVER add parenthetical notes, annotations, or disambiguation hints to sentences (e.g., do NOT write "your (formal)" or "you (plural)"). Sentences must read naturally as a native speaker would write them.
11. NEVER change sentenceSource or sentenceTarget unless the sentence itself is grammatically wrong or mistranslated. If the issue is only with distractors, only fix the distractors.

Respond ONLY with valid JSON (no markdown, no backticks) — a flat object with field names as keys and corrected values:`;
}

// Parse AI response safely (handles both string and pre-parsed object)
function parseAIResponse(raw: any): Record<string, any> {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  } else if (typeof raw === "object" && raw !== null) {
    return raw;
  }
  return {};
}

const formatDateForInput = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

export const DataQualityTab: React.FC<DataQualityTabProps> = ({ filters, onFiltersChange }) => {
  const [minAttempts, setMinAttempts] = useState(5);
  const [failureThreshold, setFailureThreshold] = useState(10);
  const [reviewStatusFilter, setReviewStatusFilter] = useState<string>("");
  const [selectedExercise, setSelectedExercise] = useState<FlaggedExercise | null>(null);
  const [aiReviewing, setAiReviewing] = useState(false);
  const [aiResult, setAiResult] = useState<{
    verdict: string;
    explanation: string;
    suggested_fix: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState<string>("failure_rate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [includeSkips, setIncludeSkips] = useState(true);

  // Local date range - defaults to all time (Jan 2024 to now)
  const [startDate, setStartDate] = useState<Date>(new Date("2024-01-01"));
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Fix generation state
  const [fixData, setFixData] = useState<Record<string, any> | null>(null);
  const [editableFixData, setEditableFixData] = useState<Record<string, string>>({});
  const [generatingFix, setGeneratingFix] = useState(false);
  const [applyingFix, setApplyingFix] = useState(false);
  const [fixApplied, setFixApplied] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);

  // Multi-select and batch review state
  const [selectedExercises, setSelectedExercises] = useState<Set<string>>(new Set());
  const [batchReviewProgress, setBatchReviewProgress] = useState<{
    current: number;
    total: number;
    results: { correct: number; incorrect: number; ambiguous: number; error: number };
  } | null>(null);
  const batchCancelledRef = useRef(false);
  const [batchSummary, setBatchSummary] = useState<string | null>(null);

  // Exercise user details state
  const [exerciseUsers, setExerciseUsers] = useState<ExerciseUser[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(false);

  // Override parent filters with local date range
  const localFilters = useMemo<AnalyticsFilters>(
    () => ({
      ...filters,
      startDate,
      endDate,
    }),
    [filters, startDate, endDate]
  );

  const { data, loading, error, refetch, setData } = useDataQualityAnalytics(
    localFilters,
    minAttempts,
    failureThreshold,
    reviewStatusFilter,
    includeSkips
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortedExercises = useCallback((): FlaggedExercise[] => {
    if (!data?.flagged_exercises) return [];
    const exercises = [...data.flagged_exercises];
    exercises.sort((a, b) => {
      const aVal = (a as any)[sortField] ?? 0;
      const bVal = (b as any)[sortField] ?? 0;
      if (typeof aVal === "string") {
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
    return exercises;
  }, [data, sortField, sortDirection]);

  const handleAIReview = async (exercise: FlaggedExercise) => {
    setAiReviewing(true);
    setAiResult(null);
    setFixData(null);
    setFixApplied(false);
    setFixError(null);

    try {
      const prompt = buildReviewPrompt(exercise);
      const result = await callOpenAI({
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
        max_tokens: 4000,
        json_mode: true,
        model: "claude-opus-4-8",
      });

      if (result?.response) {
        const parsed = parseAIResponse(result.response);

        const safeResult = {
          verdict: String(parsed.verdict || "ambiguous"),
          explanation: String(parsed.explanation || ""),
          suggested_fix: String(parsed.suggested_fix || ""),
        };
        setAiResult(safeResult);

        // Save AI review result
        await saveReview(exercise, "save_ai_review", {
          ai_verdict: safeResult.verdict,
          ai_explanation: safeResult.explanation,
          ai_suggested_fix: safeResult.suggested_fix,
        });
        updateExerciseLocally(exercise.exercise_key, "ai_reviewed");
      }
    } catch (err) {
      console.error("AI review failed:", err);
      setAiResult({
        verdict: "error",
        explanation: "AI review failed. Please try again.",
        suggested_fix: "",
      });
    } finally {
      setAiReviewing(false);
    }
  };

  const handleBatchAIReview = async () => {
    if (selectedExercises.size === 0 || !data) return;

    const exercisesToReview = data.flagged_exercises.filter((ex) =>
      selectedExercises.has(ex.exercise_key)
    );

    batchCancelledRef.current = false;
    setBatchSummary(null);
    const results = { correct: 0, incorrect: 0, ambiguous: 0, error: 0 };
    setBatchReviewProgress({ current: 0, total: exercisesToReview.length, results });

    for (let i = 0; i < exercisesToReview.length; i++) {
      if (batchCancelledRef.current) break;

      const exercise = exercisesToReview[i];
      setBatchReviewProgress({
        current: i + 1,
        total: exercisesToReview.length,
        results: { ...results },
      });

      try {
        const prompt = buildReviewPrompt(exercise);
        const result = await callOpenAI({
          messages: [{ role: "user", content: prompt }],
          temperature: 0.3,
          max_tokens: 4000,
          json_mode: true,
          model: "claude-opus-4-8",
        });

        if (result?.response) {
          const parsed = parseAIResponse(result.response);
          const verdict = String(parsed.verdict || "ambiguous");

          await saveReview(
            exercise,
            "save_ai_review",
            {
              ai_verdict: verdict,
              ai_explanation: String(parsed.explanation || ""),
              ai_suggested_fix: String(parsed.suggested_fix || ""),
            },
            true
          );

          // Update local state using functional updater to avoid stale closure
          setData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              flagged_exercises: prev.flagged_exercises.map((ex) =>
                ex.exercise_key === exercise.exercise_key
                  ? {
                      ...ex,
                      review_status: "ai_reviewed",
                      ai_verdict: verdict,
                      ai_explanation: String(parsed.explanation || ""),
                    }
                  : ex
              ),
            };
          });

          if (verdict === "correct") results.correct++;
          else if (verdict === "incorrect") results.incorrect++;
          else results.ambiguous++;
        } else {
          results.error++;
        }
      } catch (err) {
        console.error(`Batch review failed for ${exercise.exercise_key}:`, err);
        results.error++;
      }
    }

    const summary = batchCancelledRef.current
      ? `Cancelled after ${results.correct + results.incorrect + results.ambiguous + results.error} reviews`
      : `${exercisesToReview.length} reviewed: ${results.incorrect} incorrect, ${results.correct} correct, ${results.ambiguous} ambiguous${results.error ? `, ${results.error} errors` : ""}`;

    setBatchSummary(summary);
    setBatchReviewProgress(null);
    setSelectedExercises(new Set());
  };

  const handleCancelBatch = () => {
    batchCancelledRef.current = true;
  };

  const toggleExerciseSelection = (exerciseKey: string) => {
    setSelectedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(exerciseKey)) {
        next.delete(exerciseKey);
      } else {
        next.add(exerciseKey);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const sorted = getSortedExercises();
    if (selectedExercises.size === sorted.length) {
      setSelectedExercises(new Set());
    } else {
      setSelectedExercises(new Set(sorted.map((ex) => ex.exercise_key)));
    }
  };

  const handleFetchExerciseUsers = async (exercise: FlaggedExercise) => {
    setLoadingUsers(true);
    setExerciseUsers(null);
    setUsersExpanded(true);
    try {
      const users = await fetchExerciseUsers(
        exercise.exercise_key,
        startDate,
        endDate,
        includeSkips
      );
      setExerciseUsers(users);
    } catch (err) {
      console.error("Failed to fetch exercise users:", err);
      setExerciseUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleGenerateFix = async (exercise: FlaggedExercise) => {
    setGeneratingFix(true);
    setFixData(null);
    setFixApplied(false);
    setFixError(null);

    try {
      const explanation = aiResult?.explanation || exercise.ai_explanation || "";
      const prompt = buildFixPrompt(exercise, explanation);
      const result = await callOpenAI({
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
        max_tokens: 5000,
        json_mode: true,
        model: "claude-opus-4-8",
      });

      if (result?.response) {
        const parsed = parseAIResponse(result.response);
        if (Object.keys(parsed).length === 0) {
          setFixError("AI returned empty fix data. Try running AI Review first.");
        } else {
          setFixData(parsed);
          // Convert to editable strings for the form
          const editable: Record<string, string> = {};
          for (const [key, value] of Object.entries(parsed)) {
            editable[key] = typeof value === "string" ? value : JSON.stringify(value, null, 2);
          }
          setEditableFixData(editable);
        }
      }
    } catch (err) {
      console.error("Fix generation failed:", err);
      setFixError("Failed to generate fix. Please try again.");
    } finally {
      setGeneratingFix(false);
    }
  };

  // Update a single exercise's review_status in local state without refetching
  const updateExerciseLocally = (exerciseKey: string, newStatus: string) => {
    if (!data) return;
    setData({
      ...data,
      flagged_exercises: data.flagged_exercises.map((ex) =>
        ex.exercise_key === exerciseKey ? { ...ex, review_status: newStatus } : ex
      ),
    });
  };

  const handleApplyFix = async (exercise: FlaggedExercise) => {
    if (!editableFixData || Object.keys(editableFixData).length === 0) return;

    setApplyingFix(true);
    setFixError(null);

    try {
      // Parse editable values back to proper types
      const finalData: Record<string, any> = {};
      for (const [key, value] of Object.entries(editableFixData)) {
        try {
          // Try parsing as JSON (for arrays, objects, numbers)
          finalData[key] = JSON.parse(value);
        } catch {
          // Keep as string
          finalData[key] = value;
        }
      }

      const url = `${dataLoaderUri}/api/v1/analytics/data-quality`;
      await apiClient.post(url, {
        exercise_key: exercise.exercise_key,
        action: "apply_fix",
        fixed_data: finalData,
      });

      updateExerciseLocally(exercise.exercise_key, "fixed");
      resetModalState();
    } catch (err: any) {
      console.error("Failed to apply fix:", err);
      setFixError(err?.response?.data?.error || err?.message || "Failed to apply fix");
    } finally {
      setApplyingFix(false);
    }
  };

  const saveReview = async (
    exercise: FlaggedExercise,
    action: string,
    extra: Record<string, any> = {},
    silent: boolean = false
  ) => {
    if (!silent) setSaving(true);
    try {
      const url = `${dataLoaderUri}/api/v1/analytics/data-quality`;
      await apiClient.post(url, {
        exercise_key: exercise.exercise_key,
        action,
        metrics: {
          totalAttempts: exercise.total_attempts,
          correctCount: exercise.correct,
          incorrectCount: exercise.incorrect,
          failureRate: exercise.failure_rate,
          uniqueUsers: exercise.unique_users,
        },
        exercise_metadata_snapshot: exercise.metadata,
        ...extra,
      });
    } catch (err) {
      console.error("Failed to save review:", err);
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const handleConfirm = async (
    exercise: FlaggedExercise,
    action: "confirm_correct" | "confirm_incorrect"
  ) => {
    const statusMap = {
      confirm_correct: "confirmed_correct",
      confirm_incorrect: "confirmed_incorrect",
    };
    await saveReview(exercise, action);
    updateExerciseLocally(exercise.exercise_key, statusMap[action]);
    setSelectedExercise(null);
  };

  const getFailureRateClass = (rate: number) => {
    if (rate >= 80) return "failure-rate-high";
    if (rate >= 60) return "failure-rate-medium";
    return "failure-rate-low";
  };

  const getStatusBadge = (status: string | null | undefined) => {
    const displayStatus = status || "unreviewed";
    return (
      <span className={`dq-status-badge ${displayStatus}`}>{displayStatus.replace("_", " ")}</span>
    );
  };

  const resetModalState = () => {
    setSelectedExercise(null);
    setAiResult(null);
    setFixData(null);
    setEditableFixData({});
    setFixApplied(false);
    setFixError(null);
    setExerciseUsers(null);
    setLoadingUsers(false);
    setUsersExpanded(false);
  };

  if (error) {
    return (
      <div className="data-quality-tab">
        <div className="error-message">
          <h3>Error Loading Data Quality</h3>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortedExercises = getSortedExercises();

  // Determine if we should show the "Generate Fix" button
  const showGenerateFixButton =
    aiResult?.verdict === "incorrect" ||
    aiResult?.verdict === "ambiguous" ||
    selectedExercise?.ai_verdict === "incorrect" ||
    selectedExercise?.ai_verdict === "ambiguous";

  return (
    <div className="data-quality-tab">
      {/* Controls */}
      <div className="dq-controls">
        <div className="dq-control-group">
          <label>Start Date</label>
          <input
            type="date"
            value={formatDateForInput(startDate)}
            max={formatDateForInput(endDate)}
            onChange={(e) => setStartDate(new Date(e.target.value + "T00:00:00"))}
          />
        </div>
        <div className="dq-control-group">
          <label>End Date</label>
          <input
            type="date"
            value={formatDateForInput(endDate)}
            min={formatDateForInput(startDate)}
            onChange={(e) => setEndDate(new Date(e.target.value + "T23:59:59"))}
          />
        </div>
        <div className="dq-control-group">
          <label>Min Attempts: {minAttempts}</label>
          <input
            type="range"
            min="1"
            max="50"
            value={minAttempts}
            onChange={(e) => setMinAttempts(Number(e.target.value))}
          />
        </div>
        <div className="dq-control-group">
          <label>Failure Threshold: {failureThreshold}%</label>
          <input
            type="range"
            min="10"
            max="100"
            value={failureThreshold}
            onChange={(e) => setFailureThreshold(Number(e.target.value))}
          />
        </div>
        <div className="dq-control-group">
          <label>Review Status</label>
          <select
            value={reviewStatusFilter}
            onChange={(e) => setReviewStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="unreviewed">Unreviewed</option>
            <option value="flagged">Flagged</option>
            <option value="ai_reviewed">AI Reviewed</option>
            <option value="confirmed_correct">Confirmed Correct</option>
            <option value="confirmed_incorrect">Confirmed Incorrect</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>
        <div className="dq-control-group dq-checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={includeSkips}
              onChange={(e) => setIncludeSkips(e.target.checked)}
            />
            Include skips as failures
          </label>
        </div>
        <button
          className="dq-scan-btn"
          onClick={() => {
            setSelectedExercises(new Set());
            setBatchSummary(null);
            refetch();
          }}
          disabled={loading}
        >
          {loading ? "Scanning..." : "Scan"}
        </button>
        {selectedExercises.size > 0 && !batchReviewProgress && (
          <button className="dq-scan-btn dq-batch-btn" onClick={handleBatchAIReview}>
            AI Review Selected ({selectedExercises.size})
          </button>
        )}
        {batchReviewProgress && (
          <span className="dq-batch-progress">
            Reviewing {batchReviewProgress.current}/{batchReviewProgress.total}...
            <button className="dq-cancel-btn" onClick={handleCancelBatch}>
              Cancel
            </button>
          </span>
        )}
        {batchSummary && <span className="dq-batch-summary">{batchSummary}</span>}
      </div>

      {/* Summary KPIs */}
      <div className="dq-summary">
        <h2>Data Quality Overview</h2>
        <div className="kpi-grid">
          {loading ? (
            <>
              <KPICard title="Total Flagged" value="0" loading={true} />
              <KPICard title="AI Reviewed" value="0" loading={true} />
              <KPICard title="Confirmed Bad" value="0" loading={true} />
              <KPICard title="Confirmed OK" value="0" loading={true} />
            </>
          ) : data?.summary ? (
            <>
              <KPICard
                title="Total Flagged"
                value={data.summary.total_flagged.toString()}
                subtitle={`>=${failureThreshold}% failure rate`}
                icon="🚩"
              />
              <KPICard
                title="AI Reviewed"
                value={data.summary.ai_reviewed.toString()}
                subtitle="Automatically checked"
                icon="🤖"
              />
              <KPICard
                title="Confirmed Bad"
                value={data.summary.confirmed_incorrect.toString()}
                subtitle="Needs regeneration"
                icon="❌"
              />
              <KPICard
                title="Confirmed OK"
                value={data.summary.confirmed_correct.toString()}
                subtitle="Just hard exercises"
                icon="✅"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Flagged Exercises Table */}
      {!loading && sortedExercises.length === 0 ? (
        <div className="dq-empty">
          <p>No exercises found matching criteria.</p>
          <p>Try lowering the failure threshold or increasing the date range.</p>
        </div>
      ) : (
        <div className="dq-table-container">
          <div className="dq-table-header">
            <h3>Flagged Exercises ({sortedExercises.length})</h3>
          </div>
          <table className="dq-table">
            <thead>
              <tr>
                <th className="dq-checkbox-col" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={
                      sortedExercises.length > 0 &&
                      selectedExercises.size === sortedExercises.length
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort("component")}>Component</th>
                <th onClick={() => handleSort("section")}>Section</th>
                <th onClick={() => handleSort("word_id")}>Word ID</th>
                <th onClick={() => handleSort("failure_rate")}>Failure Rate</th>
                <th onClick={() => handleSort("total_attempts")}>Attempts</th>
                <th onClick={() => handleSort("skips")}>Skips</th>
                <th onClick={() => handleSort("unique_users")}>Users</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="dq-loading">
                      <div className="dq-spinner" /> Loading...
                    </div>
                  </td>
                </tr>
              ) : (
                sortedExercises.map((exercise, idx) => (
                  <tr
                    key={exercise.exercise_key || idx}
                    onClick={() => {
                      resetModalState();
                      setSelectedExercise(exercise);
                    }}
                  >
                    <td className="dq-checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedExercises.has(exercise.exercise_key)}
                        onChange={() => toggleExerciseSelection(exercise.exercise_key)}
                      />
                    </td>
                    <td>{exercise.component}</td>
                    <td title={exercise.section}>
                      {exercise.section.length > 25
                        ? exercise.section.slice(0, 25) + "..."
                        : exercise.section}
                    </td>
                    <td title={exercise.word_id}>
                      {exercise.word_id.length > 20
                        ? exercise.word_id.slice(0, 20) + "..."
                        : exercise.word_id}
                    </td>
                    <td className={getFailureRateClass(exercise.failure_rate)}>
                      {exercise.failure_rate}%
                    </td>
                    <td>{exercise.total_attempts}</td>
                    <td>{exercise.skips || 0}</td>
                    <td>{exercise.unique_users}</td>
                    <td>{getStatusBadge(exercise.review_status)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="dq-action-btn ai-review"
                        onClick={() => {
                          resetModalState();
                          setSelectedExercise(exercise);
                          handleAIReview(exercise);
                        }}
                        disabled={aiReviewing || !!batchReviewProgress}
                      >
                        AI Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedExercise && (
        <div className="dq-modal-overlay" onClick={resetModalState}>
          <div className="dq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dq-modal-header">
              <h2>Exercise Detail</h2>
              <button className="dq-modal-close" onClick={resetModalState}>
                &times;
              </button>
            </div>

            {/* Stats */}
            <div className="dq-modal-section">
              <h3>Performance Metrics</h3>
              <div className="dq-stats-grid">
                <div className="dq-stat-item">
                  <div
                    className={`dq-stat-value ${getFailureRateClass(selectedExercise.failure_rate)}`}
                  >
                    {selectedExercise.failure_rate}%
                  </div>
                  <div className="dq-stat-label">Failure Rate</div>
                </div>
                <div className="dq-stat-item">
                  <div className="dq-stat-value">{selectedExercise.total_attempts}</div>
                  <div className="dq-stat-label">Total Attempts</div>
                </div>
                <div className="dq-stat-item">
                  <div className="dq-stat-value">{selectedExercise.correct}</div>
                  <div className="dq-stat-label">Correct</div>
                </div>
                <div className="dq-stat-item">
                  <div className="dq-stat-value">{selectedExercise.incorrect}</div>
                  <div className="dq-stat-label">Incorrect</div>
                </div>
                <div className="dq-stat-item">
                  <div className="dq-stat-value">{selectedExercise.skips || 0}</div>
                  <div className="dq-stat-label">Skips</div>
                </div>
                <div
                  className="dq-stat-item dq-stat-clickable"
                  onClick={() => {
                    if (usersExpanded) {
                      setUsersExpanded(false);
                    } else {
                      handleFetchExerciseUsers(selectedExercise);
                    }
                  }}
                  title="Click to view user details"
                >
                  <div className="dq-stat-value dq-stat-link">{selectedExercise.unique_users}</div>
                  <div className="dq-stat-label">Unique Users {usersExpanded ? "▲" : "▼"}</div>
                </div>
              </div>

              {/* Expandable User Details */}
              {usersExpanded && (
                <div className="dq-users-section">
                  {loadingUsers ? (
                    <div className="dq-loading">
                      <div className="dq-spinner" /> Loading users...
                    </div>
                  ) : exerciseUsers && exerciseUsers.length > 0 ? (
                    <table className="dq-users-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Correct</th>
                          <th>Incorrect</th>
                          <th>Skips</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exerciseUsers.map((user, idx) => (
                          <tr key={user.user_id || idx}>
                            <td>
                              {[user.first_name, user.last_name].filter(Boolean).join(" ") || "-"}
                            </td>
                            <td>{user.user_id}</td>
                            <td>{user.correct}</td>
                            <td>{user.incorrect}</td>
                            <td>{user.skips}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : exerciseUsers ? (
                    <p style={{ color: "#999", fontSize: "0.9em" }}>
                      No user data found for this exercise.
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            {/* Exercise Info */}
            <div className="dq-modal-section">
              <h3>Exercise Info</h3>
              <p>
                <strong>Component:</strong> {selectedExercise.component}
              </p>
              <p>
                <strong>Section:</strong> {selectedExercise.section}
              </p>
              <p>
                <strong>Word ID:</strong> {selectedExercise.word_id}
              </p>
              <p>
                <strong>Status:</strong> {getStatusBadge(selectedExercise.review_status)}
              </p>
            </div>

            {/* Metadata */}
            <div className="dq-modal-section">
              <h3>Exercise Metadata</h3>
              {selectedExercise.metadata ? (
                <div className="dq-metadata">
                  <pre>{JSON.stringify(selectedExercise.metadata, null, 2)}</pre>
                </div>
              ) : (
                <div className="dq-metadata">
                  <p style={{ color: "#999" }}>
                    No metadata found. Component "{selectedExercise.component}" may not have a table
                    mapping, or the section/word_id format didn't match.
                  </p>
                </div>
              )}
            </div>

            {/* AI Review */}
            <div className="dq-modal-section">
              <h3>AI Review</h3>
              {aiReviewing ? (
                <div className="dq-loading">
                  <div className="dq-spinner" /> Running AI review...
                </div>
              ) : aiResult ? (
                <div className="dq-ai-result">
                  <div className={`dq-ai-verdict ${aiResult.verdict}`}>{aiResult.verdict}</div>
                  <div className="dq-ai-explanation">{aiResult.explanation}</div>
                  {aiResult.suggested_fix && (
                    <div className="dq-ai-fix">
                      <strong>Suggested Fix:</strong> {aiResult.suggested_fix}
                    </div>
                  )}
                </div>
              ) : selectedExercise.ai_verdict ? (
                <div className="dq-ai-result">
                  <div className={`dq-ai-verdict ${selectedExercise.ai_verdict}`}>
                    {selectedExercise.ai_verdict}
                  </div>
                  <div className="dq-ai-explanation">{selectedExercise.ai_explanation || ""}</div>
                  {selectedExercise.ai_suggested_fix && (
                    <div className="dq-ai-fix">
                      <strong>Suggested Fix:</strong> {selectedExercise.ai_suggested_fix}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  className="dq-action-btn ai-review"
                  onClick={() => handleAIReview(selectedExercise)}
                  disabled={aiReviewing}
                >
                  Run AI Review
                </button>
              )}
            </div>

            {/* Generate Fix Section */}
            {showGenerateFixButton && !fixApplied && (
              <div className="dq-modal-section">
                <h3>Generate & Apply Fix</h3>

                {generatingFix ? (
                  <div className="dq-loading">
                    <div className="dq-spinner" /> Generating corrected data...
                  </div>
                ) : fixData ? (
                  <div className="dq-fix-preview">
                    <p className="dq-fix-instructions">
                      Review and edit the corrected values below, then click "Apply Fix" to update
                      the database.
                    </p>
                    <div className="dq-fix-fields">
                      {Object.entries(editableFixData).map(([field, value]) => {
                        const originalValue = selectedExercise.metadata?.[field];
                        const originalDisplay =
                          originalValue !== undefined
                            ? typeof originalValue === "string"
                              ? originalValue
                              : JSON.stringify(originalValue, null, 2)
                            : "(not set)";
                        return (
                          <div key={field} className="dq-fix-field">
                            <label className="dq-fix-field-label">{field}</label>
                            <div className="dq-fix-field-current">
                              <span className="dq-fix-label-tag">Current:</span>
                              <pre className="dq-fix-value-old">{originalDisplay}</pre>
                            </div>
                            <div className="dq-fix-field-new">
                              <span className="dq-fix-label-tag">New:</span>
                              <textarea
                                className="dq-fix-value-input"
                                value={value}
                                onChange={(e) =>
                                  setEditableFixData((prev) => ({
                                    ...prev,
                                    [field]: e.target.value,
                                  }))
                                }
                                rows={
                                  value.includes("\n")
                                    ? Math.min(value.split("\n").length + 1, 8)
                                    : 2
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {fixError && <div className="dq-fix-error">{fixError}</div>}

                    <div className="dq-fix-actions">
                      <button
                        className="dq-action-btn apply-fix"
                        onClick={() => handleApplyFix(selectedExercise)}
                        disabled={applyingFix}
                      >
                        {applyingFix ? "Applying..." : "Apply Fix to Database"}
                      </button>
                      <button
                        className="dq-action-btn regenerate-fix"
                        onClick={() => handleGenerateFix(selectedExercise)}
                        disabled={generatingFix}
                      >
                        Regenerate Fix
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {fixError && <div className="dq-fix-error">{fixError}</div>}
                    <button
                      className="dq-action-btn generate-fix"
                      onClick={() => handleGenerateFix(selectedExercise)}
                      disabled={generatingFix}
                    >
                      Generate Fix
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Fix Applied Success */}
            {fixApplied && (
              <div className="dq-modal-section">
                <div className="dq-fix-success">
                  Fix applied successfully! The exercise data has been updated in the database.
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="dq-modal-actions">
              <button
                className="dq-action-btn mark-correct"
                onClick={() => handleConfirm(selectedExercise, "confirm_correct")}
                disabled={saving}
              >
                {saving ? "Saving..." : "Confirm Correct (Hard Exercise)"}
              </button>
              <button
                className="dq-action-btn mark-incorrect"
                onClick={() => handleConfirm(selectedExercise, "confirm_incorrect")}
                disabled={saving}
              >
                {saving ? "Saving..." : "Confirm Incorrect (Bad Data)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityTab;
